import { FC, MouseEvent as ReactMouseEvent, FormEvent, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { prefetchBannerState } from 'src/App/AppRouter/Layout/bannerState'
import { HOME_ROUTE } from 'src/constants/routes'
import en from 'src/constants/en'
import { useLoginMutation } from 'src/reactQuery/useLogInMutation'
import { setDataToLocalStorage } from 'src/localStorage/setDataToLocalStorage'
import { LocalStorageKey } from 'src/localStorage/enums/localStorageKey.enum'
import { AnalyticsApi } from 'src/services/analyticsApi'
import { SuggestionsApi } from 'src/services/suggestionsApi'
import { useGetSelfQuery } from 'src/reactQuery/useGetSelfQuery'
import { AdminApi } from 'src/services/adminApi'
import { trackLogin } from 'src/services/trackingApi'
import { CheckBox } from 'src/components/CheckBox'
import Button from 'src/components/Button'
import styles from './login.module.scss'
import EyeIcon from '../../assets/svg/eye.svg'
import EyeClosedIcon from '../../assets/svg/eyeclosed.svg'
import BlueVector from '../../assets/svg/login-vector-blue.svg'
import OrangeVector from '../../assets/svg/login-orange-vector.svg'
import LoginLogo from '../../assets/svg/Logo-login.svg'

export const Login: FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()

  // Deep link preserved by PrivateRoute when it redirected here.
  const from =
    (location.state as { from?: { pathname?: string } } | null)?.from
      ?.pathname || HOME_ROUTE

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [checked, setChecked] = useState(true)
  const [passwordVisible, setPasswordVisible] = useState(false)

  type MouseEventType = ReactMouseEvent<HTMLButtonElement, MouseEvent>

  const { data: userData } = useGetSelfQuery()

  const loginMutation = useLoginMutation({
    onError: (error) => {
      if (error?.response?.status === 401) {
        toast.error(en.INVALID_CREDENTIALS)
      }
    },
    onSuccess: async (data) => {
      const { sessionId } = data

      setDataToLocalStorage(LocalStorageKey.SESSION_ID, sessionId)

      AdminApi.refresh()

      AnalyticsApi.refresh()

      SuggestionsApi.refresh()

      trackLogin()

      // Resolve the banner before entering the app. This is the one moment
      // there is no cached state to read, so without it a first-ever login
      // lands on an un-collapsed sidebar and then visibly collapses it — the
      // behaviour we are removing. The wait happens under the login button's
      // existing pending state, where users already expect to wait, and it is
      // best-effort: a failure just falls through to the old late reveal
      // rather than trapping anyone on the login screen.
      await prefetchBannerState(queryClient)

      navigate(from)
    },
  })

  const onLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    loginMutation.mutate({
      password,
      username: email,
    })
  }

  const isAuthenticated = !!userData?.user

  const handleChangeCheck = () => {
    setChecked(!checked)
  }

  if (isAuthenticated) {
    return (
      <Navigate
        to={{
          pathname: from,
        }}
      />
    )
  }

  const togglePassordVisibility = (e: MouseEventType) => {
    e.preventDefault()
    e.stopPropagation()

    setPasswordVisible(!passwordVisible)
  }

  return (
    <div className={styles.loginContainer}>
      <div className={styles.loginLeft}>
        <div aria-hidden="true" className={styles.vectorLayer}>
          <img alt="" className={styles.vectorBlue} src={BlueVector} />
          <img alt="" className={styles.vectorOrange} src={OrangeVector} />
        </div>

        <div className={styles.card}>
          <div className={styles.logoRow}>
            <img alt="Hapttic" className={styles.logoImg} src={LoginLogo} />
          </div>

          <div className={styles.cardHeading}>
            <h2 className={styles.title}>Welcome Back</h2>
            <div className={styles.subtitle}>Continue to your dashboard</div>
          </div>

          <form className={styles.form} onSubmit={(e) => onLogin(e)}>
            <div className={styles.inputContainer}>
              <label className={styles.label}>{en.email}</label>

              <input
                className={styles.input}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@gmail.com"
                type="email"
              />
            </div>

            <div className={styles.inputContainer}>
              <label className={styles.label}>{en.password}</label>

              <div className={styles.passwordWrapper}>
                <input
                  className={styles.input}
                  name=""
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={en.min8Characters}
                  type={passwordVisible ? 'text' : 'password'}
                />

                <button
                  className={styles.passwordVisibilityButton}
                  onClick={(e) => togglePassordVisibility(e)}
                  type="button"
                >
                  <img
                    alt="password visibility"
                    className={styles.passwordVisibilityIcon}
                    src={passwordVisible ? EyeIcon : EyeClosedIcon}
                  />
                </button>
              </div>
            </div>

            <div className={styles.additionalOptions}>
              <div className={styles.checkBox}>
                <CheckBox isChecked={checked} onChange={handleChangeCheck} />

                <label className={styles.checkboxLabel} htmlFor="check">
                  {en.rememberMe}
                </label>
              </div>

              <a className={styles.forgotPasswordLink} href="#">
                {en.forgotPassword}
              </a>
            </div>

            {/* The handler now awaits the banner prefetch before navigating,
                so the button had to stop looking idle while it waits. */}
            <Button
              className={styles.submit}
              isDisabled={loginMutation.isPending}
              type="submit"
            >
              {loginMutation.isPending ? en.signingIn : en.login}
            </Button>

            {/* TODO: Add after sign up will be live */}
            {/* <div className={styles.signUpLink}}>
            Not registered?{' '}
            <span>
              <Link
                to={SIGNUP_ROUTE}
              >
                Create an account
              </Link>
            </span>
          </div> */}
          </form>
        </div>
      </div>
    </div>
  )
}
